import React, { useState, useRef } from "react";
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Typography,
} from "@mui/material";
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useDispatch, useSelector } from "react-redux";
import { uploadPersonDocument } from "../../store/actions/documentActions";
import { RootState } from "../../store/reducers";

const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];
const MAX_SIZE = 10 * 1024 * 1024; // 10MB

interface Props {
    open: boolean;
    onClose: () => void;
    personId: number;
    documentTypes: iDocumentType[];
    initialFile?: File;
}

const DocumentUploadDialog = (props: Props) => {
    const dispatch = useDispatch();
    const isUploading = useSelector((state: RootState) => state.cadastros.isUploadingDocument);

    const [selectedTypeId, setSelectedTypeId] = useState<number | ''>('');
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [error, setError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Pre-populate with scanned file when provided
    React.useEffect(() => {
        if (props.initialFile) {
            setSelectedFile(props.initialFile);
        }
    }, [props.initialFile]);

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        setError(null);
        const file = e.target.files?.[0];
        if (!file) return;

        if (!ALLOWED_TYPES.includes(file.type)) {
            setError('Tipo de arquivo não permitido. Aceitos: PDF, JPG, PNG');
            return;
        }
        if (file.size > MAX_SIZE) {
            setError('Arquivo excede o tamanho máximo de 10MB');
            return;
        }
        setSelectedFile(file);
    };

    const handleUpload = async () => {
        if (!selectedTypeId || !selectedFile || !props.personId) return;
        setError(null);

        try {
            await dispatch(uploadPersonDocument(props.personId, selectedTypeId as number, selectedFile) as any);
            handleClose();
        } catch (e: any) {
            setError(e?.response?.data?.error?.message || 'Erro ao enviar documento');
        }
    };

    const handleClose = () => {
        setSelectedTypeId('');
        setSelectedFile(null);
        setError(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        props.onClose();
    };

    return (
        <Dialog open={props.open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Adicionar Documento</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Tipo de Documento</InputLabel>
                        <Select
                            value={selectedTypeId}
                            label="Tipo de Documento"
                            onChange={(e) => setSelectedTypeId(e.target.value as number)}
                        >
                            {props.documentTypes
                                .filter((dt: iDocumentType) => dt.active)
                                .map((dt: iDocumentType) => (
                                    <MenuItem key={dt.id} value={dt.id}>
                                        {dt.name} {dt.required ? '(Obrigatório)' : ''}
                                    </MenuItem>
                                ))
                            }
                        </Select>
                    </FormControl>

                    <Button
                        variant="outlined"
                        component="label"
                        startIcon={<UploadFileIcon />}
                    >
                        {selectedFile ? selectedFile.name : 'Selecionar Arquivo'}
                        <input
                            ref={fileInputRef}
                            type="file"
                            hidden
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={handleFileSelect}
                        />
                    </Button>

                    {selectedFile && (
                        <Typography variant="body2" color="text.secondary">
                            {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                        </Typography>
                    )}

                    {error && <Alert severity="error">{error}</Alert>}

                    <Typography variant="caption" color="text.secondary">
                        Formatos aceitos: PDF, JPG, PNG. Tamanho máximo: 10MB.
                    </Typography>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={isUploading}>Cancelar</Button>
                <Button
                    variant="contained"
                    onClick={handleUpload}
                    disabled={!selectedTypeId || !selectedFile || isUploading}
                >
                    {isUploading ? 'Enviando...' : 'Enviar'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DocumentUploadDialog;
